import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { orderService } from '../../services/orderService';
import Card from '../../components/common/Card';
import DataTable from '../../components/common/DataTable';
import SearchInput from '../../components/common/SearchInput';
import Select from '../../components/common/Select';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';

const PAGE_SIZE = 20;

const ResellerOrders = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orderService.getAll({
        status: status || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = q
    ? rows.filter((o) => `${o.id}`.includes(q) || (o.customer?.name || '').toLowerCase().includes(q.toLowerCase()))
    : rows;

  const columns = [
    {
      key: 'id',
      header: 'Pedido',
      width: 100,
      render: (o) => <span className="font-mono text-sm">#{o.id}</span>,
    },
    {
      key: 'createdAt',
      header: 'Data',
      render: (o) => (
        <span className="text-sm text-app-text-secondary">{formatDateTime(o.createdAt)}</span>
      ),
    },
    {
      key: 'totalToPay',
      header: 'Você paga',
      align: 'right',
      render: (o) => <span className="font-semibold">{formatPrice(o.totalToPay)}</span>,
    },
    {
      key: 'totalCommission',
      header: 'Comissão',
      align: 'right',
      render: (o) => (
        <span className="font-semibold text-success">+{formatPrice(o.totalCommission)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (o) => <StatusBadge domain="order" status={o.status} />,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-extrabold text-app-text mb-6">Meus pedidos</h1>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <SearchInput
              value={q}
              onChange={setQ}
              placeholder="Buscar por #ID ou cliente…"
              debounceMs={200}
            />
          </div>
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'awaiting_approval', label: 'Aguardando aprovação' },
              { value: 'approved', label: 'Aprovado' },
              { value: 'paid', label: 'Pago' },
              { value: 'shipped', label: 'Enviado' },
              { value: 'delivered', label: 'Entregue' },
              { value: 'rejected', label: 'Rejeitado' },
            ]}
          />
        </div>
      </Card>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        total={q ? filtered.length : total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onRowClick={(o) => navigate(`/revendedor/painel/pedidos/${o.id}`)}
        empty={
          <EmptyState
            icon={ShoppingBag}
            title={q || status ? 'Nenhum pedido' : 'Sem pedidos ainda'}
            description={q || status ? 'Ajuste os filtros.' : 'Comece compartilhando produtos do catálogo.'}
          />
        }
      />
    </div>
  );
};

export default ResellerOrders;
